import { Sprite, Texture, Container} from 'pixi.js';
import {ISlot} from 'pixi-spine';
import {ReelConstants} from '@gongxigames/framework/slot/utils/ReelConstants';
import {ReelSymbol} from '@gongxigames/framework/slot/modules/reels/ReelSymbol';
import {inject, injectable} from 'inversify';
import {BitmapText, CoreDI, IAnimation, IService, MapKeyNumber, MovieClip, ObjectBuilder, Spine, SystemComponentType} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';

export type PrizeData = {
    prizeIndex: number;
    superPrizeIndex?: number;
    multiplier?: number;
    position?: number;
    isRespin?: boolean;
};

export type IPrizeSymbolsUtilsSettings = {
    prizeLowFrame: number;
    prizeHighFrames: Record<number, number>;
    prizeImages: MapKeyNumber<string>;
    prizeSlotNames: string[];
    prizeIndexToWin: (prizeIndex: number, position?: number) => number;
    getRespinBonusCaller?: () => IRespinBonusCaller;
    getPrizeNumbersName: (prizeIndex: number) => string;
    getPrizeNumbersPrefix: (prizeIndex: number) => string;
    getFormattedValue: (value: number, prizeData: PrizeData) => string;
};

export interface IRespinBonusCaller {
    getActualPrizeWin: (position: number) => number;
}

@injectable()
export class PrizeSymbolsUtils implements IService {
    public static readonly NAME = DI.PrizeSymbolsUtils;
    public type: SystemComponentType = 'service';

    @inject(CoreDI.ObjectBuilder)
    private objectBuilder!: ObjectBuilder;

    private settings!: IPrizeSymbolsUtilsSettings;

    public setValueForPrizeAnimation(
        animation: IAnimation | Spine,
        prizeData: PrizeData,
        prizeDisplayObject: Container,
        offSet = [0, 0]
    ): void {
        prizeDisplayObject.x -= prizeDisplayObject.width / 2 + offSet[0];
        prizeDisplayObject.y -= prizeDisplayObject.height / 2 + offSet[1];
        this.addTextureToSlot(animation, prizeDisplayObject, prizeData);
    }

    public addTextureToSlot(animation: IAnimation | Spine, texture: Container, prizeData: PrizeData): void {
        this.settings.prizeSlotNames.forEach((slotName) => {
            const animationContainer = animation instanceof Spine ? animation : animation.container as Spine;
            const prizeTextSlot = animationContainer.skeleton.findSlot(slotName);
            const setPrizeValue = (slot: ISlot, prizeData: PrizeData) => {
                prizeTextSlot.currentSprite.scale.set(0.5, -0.5);
                prizeTextSlot.currentSprite.texture = Texture.EMPTY;
                prizeTextSlot.currentSprite.removeChildren();

                if (prizeData.prizeIndex > 0) {
                    prizeTextSlot.currentSprite.addChild(texture);
                }
            };

            if (prizeTextSlot) {
                setPrizeValue(prizeTextSlot, prizeData);

                if (prizeData.superPrizeIndex && prizeData.superPrizeIndex > 0) {
                    const prizeIndex = prizeData.superPrizeIndex;

                    animationContainer.state.addListener({
                        event: () => setPrizeValue(prizeTextSlot, {...prizeData, prizeIndex})
                    });
                }
            }
        });
    }

    public getJackpotTypePrize(prizeData: PrizeData): Container {
        const {prizeImages} = this.settings;
        const highPrizes = this.objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.jpImages);

        return highPrizes.getChildByName(`${prizeImages[prizeData.prizeIndex]}`);
    }

    public getTextPrize(prizeData: PrizeData): BitmapText {
        const {getPrizeNumbersName, getPrizeNumbersPrefix, getFormattedValue} = this.settings;
        const prizeValue = this.getPrizeValue(prizeData);
        const prizeName = getPrizeNumbersName(prizeData.prizeIndex);
        const prizePrefix = getPrizeNumbersPrefix(prizeData.prizeIndex);
        const prizeTexture = new BitmapText(prizeName, this.objectBuilder, undefined, prizePrefix);

        prizeTexture.text = '' + getFormattedValue(prizeValue, prizeData);

        return prizeTexture;
    }

    public getStaticPrizeMC(symbol: ReelSymbol, chipSymbol?: Sprite): MovieClip {
        const currentSymbol = chipSymbol ? chipSymbol : symbol.symbol.getChildAt(symbol.symbolType) as Sprite;

        return currentSymbol.getChildByName<MovieClip>('win');
    }

    public setJackpotPrizeToSymbol(prizeValueMc: MovieClip, prizeIndex: number): void {
        const {prizeHighFrames} = this.settings;

        prizeValueMc.gotoAndStop(prizeHighFrames[prizeIndex]);
    }

    public setNumberPrizeToSymbol(
        prizeValueMc: MovieClip,
        prizeData: PrizeData,
        chipSymbol?: Sprite
    ): void {
        prizeValueMc.gotoAndStop(this.settings.prizeLowFrame);
        const prizeNumberValueSlot = prizeValueMc.getChildByName<Sprite>('value');

        prizeNumberValueSlot.removeChildren();

        if (prizeData.prizeIndex >= 0) {
            const text = this.getTextPrize(prizeData);

            if (text.width > ReelConstants.SYMBOL.width) {
                text.scale.set(ReelConstants.SYMBOL.width / text.width, 1);
            }

            const currentSymbolX = chipSymbol ? 0 : prizeValueMc.parent.x;

            prizeNumberValueSlot.x = (ReelConstants.SYMBOL.width - text.width) / 2 - currentSymbolX;
            prizeNumberValueSlot.addChild(text);
        }
    }

    public setPrizeSettings(settings: IPrizeSymbolsUtilsSettings): void {
        this.settings = settings;
    }

    protected getPrizeValue(prizeData: PrizeData): number {
        const {getRespinBonusCaller, prizeIndexToWin} = this.settings;

        if (getRespinBonusCaller && typeof prizeData.position === 'number' && prizeData.position >= 0) {
            return getRespinBonusCaller().getActualPrizeWin(prizeData.position);
        }

        return prizeIndexToWin(prizeData.prizeIndex, prizeData.position);
    }
}
